import { styled } from '@linaria/react';
import dayjs from 'dayjs';
import type { ReactElement } from 'react';
import { PaceStatus } from '../../../../api/work-periods-client/work-periods-client.type';
import { formatDate, safeParseDateInput } from '../../../../helpers/date-time-helpers';
import { buildWorkPaceQuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links';
import type { QuickLink } from '../../../../helpers/flora-quick-links/flora-quick-links.type';
import { filterPaceData, transformForPaceChart } from '../../../work-periods/work-pace/pace-section.helpers';
import { Metric, type PaceChartDataPoint, WorkPaceChartType } from '../../../work-periods/work-periods.type';
import type {
  BoardConfig,
  PaceLineKey,
  WorkPaceArtifactChartDataPoint,
  WorkPaceArtifactDotProps,
  WorkPaceArtifactRawChartData,
  WorkPaceArtifactTooltipProps,
  WorkPacePayload,
  WorkPaceTooltipStatusProps,
  WorkPaceWorkPeriod,
} from './work-pace-artifact.type';

const PACE_SERIES_KEYS = new Set(['actual', 'ideal', 'remaining', 'complete', 'total']);
const ALLOWED_QUICK_LINK_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Checks whether an artifact data object has the expected Work Pace wrapper shape.
 *
 * @param {Record<string, unknown>} data - Raw Flora artifact data payload.
 * @returns {boolean} True when the payload exposes a work_periods array.
 */
function isWorkPacePayload(data: Record<string, unknown>): data is Record<string, unknown> & WorkPacePayload {
  return Array.isArray(data.work_periods);
}

/**
 * Returns the short user-facing units label for the selected Work Pace metric.
 *
 * @param {Metric} metric - Work Pace metric enum value.
 * @returns {string} Lowercase metric label used in chart axes and tooltips.
 */
function getMetricLabel(metric: Metric): string {
  return metric === Metric.Tasks ? 'tasks' : 'points';
}

/**
 * Reads the initial work-period index from the export-route `initialState` seed.
 * Defaults to 0 (first work period) when no seed is provided or the value is invalid.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed from the export route.
 * @returns {number} A non-negative integer index.
 */
function getInitialWorkPeriodIndex(initialState: Record<string, unknown> | undefined): number {
  const value = initialState?.workPeriodIndex;
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
  return 0;
}

/**
 * Reads the initial metric (tasks vs points) from the export-route `initialState` seed.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed from the export route.
 * @returns {Metric} `Metric.Points` when the seed is `'points'`; `Metric.Tasks` otherwise.
 */
function getInitialMetric(initialState: Record<string, unknown> | undefined): Metric {
  return initialState?.metric === 'points' ? Metric.Points : Metric.Tasks;
}

/**
 * Reads the initial chart type (burn-up vs burn-down) from the export-route `initialState` seed.
 *
 * @param {Record<string, unknown> | undefined} initialState - Optional state seed from the export route.
 * @returns {WorkPaceChartType} `WorkPaceChartType.BurnDown` when the seed is `'burn_down'`; `BurnUp` otherwise.
 */
function getInitialChartType(initialState: Record<string, unknown> | undefined): WorkPaceChartType {
  return initialState?.chartType === 'burn_down' ? WorkPaceChartType.BurnDown : WorkPaceChartType.BurnUp;
}

/**
 * Opens a quick-link URL in a new tab after validating it is an http(s) URL.
 *
 * @param {string} url - Raw quick-link URL.
 */
function openArtifactQuickLink(url: string): void {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) return;

  try {
    const normalizedUrl = new URL(trimmedUrl, window.location.origin);
    if (!ALLOWED_QUICK_LINK_PROTOCOLS.has(normalizedUrl.protocol)) return;

    window.open(normalizedUrl.toString(), '_blank', 'noopener,noreferrer');
  } catch {
    return;
  }
}

/**
 * Converts a pace status enum value into stable display text.
 *
 * @param {PaceStatus | undefined} status - Optional pace status from the backend payload.
 * @returns {string} Display text for the status, defaulting to Off Track for missing or unknown values.
 */
function getStatusText(status?: PaceStatus): string {
  if (status === PaceStatus.OnTrack) return 'On Track';
  if (status === PaceStatus.AtRisk) return 'At Risk';
  return 'Off Track';
}

/**
 * Builds Work Pace quick links scoped to the currently selected artifact work period.
 *
 * @param {WorkPaceWorkPeriod} workPeriod - Currently selected Work Pace artifact work period.
 * @param {Record<string, unknown> | undefined} config - Raw artifact config containing board context.
 * @returns {QuickLink[]} Quick links for opening the selected work period in the Work Periods module.
 */
function buildScopedWorkPaceQuickLinks(workPeriod: WorkPaceWorkPeriod, config?: Record<string, unknown>): QuickLink[] {
  const scopedConfig = config ? { ...config } : {};
  const rawBoards = scopedConfig.boards;
  const boards = Array.isArray(rawBoards) ? (rawBoards as BoardConfig[]) : [];
  const scopedBoards = boards.map((board) => ({
    ...board,
    work_period_ids: board.work_period_ids?.includes(workPeriod.id) ? [workPeriod.id] : board.work_period_ids,
  }));

  return buildWorkPaceQuickLink({ work_periods: [workPeriod] } as unknown as Record<string, unknown>, {
    ...scopedConfig,
    boards: scopedBoards,
  });
}

/**
 * Nullifies Work Pace line-series values for dates after today.
 * Fails closed: if a date cannot be parsed, series values are also nullified.
 *
 * @param {PaceChartDataPoint[]} chartData - Work Pace chart rows.
 * @returns {WorkPaceArtifactChartDataPoint[]} Chart rows with future series values nullified.
 */
function nullifyFuturePaceChartData(chartData: PaceChartDataPoint[]): WorkPaceArtifactChartDataPoint[] {
  if (!chartData?.length) return [];

  const today = dayjs().endOf('day');

  return chartData.map((dataPoint) => {
    const dataPointDate = safeParseDateInput(dataPoint.date);
    const shouldNullify = !dataPointDate || dataPointDate.valueOf() > today.valueOf();

    if (!shouldNullify) return dataPoint;

    return Object.fromEntries(
      Object.entries(dataPoint).map(([key, value]) => (PACE_SERIES_KEYS.has(key) ? [key, null] : [key, value])),
    ) as WorkPaceArtifactChartDataPoint;
  });
}

/**
 * Converts raw date-keyed flow_by_pace payload data into artifact chart rows.
 *
 * @param {WorkPaceArtifactRawChartData} chartData - Raw backend flow_by_pace payload for one work period.
 * @param {Metric} metric - Selected metric used to choose tasks_* or points_* fields.
 * @returns {WorkPaceArtifactChartDataPoint[]} Recharts-ready Work Pace rows with future values nullified.
 */
function buildWorkPaceArtifactChartData(
  chartData: WorkPaceArtifactRawChartData,
  metric: Metric,
): WorkPaceArtifactChartDataPoint[] {
  const transformed = transformForPaceChart(filterPaceData(chartData, metric));
  return nullifyFuturePaceChartData(transformed);
}

/**
 * Builds a status-colored Recharts dot renderer for the primary Work Pace line.
 *
 * @param {PaceLineKey} dataKey - Primary series key for the active chart type.
 * @param {string} stroke - Dot outline color.
 * @param {number} radius - Dot radius in pixels.
 * @returns {(props: WorkPaceArtifactDotProps) => ReactElement} Recharts-compatible dot render function.
 */
function renderStatusDot(
  dataKey: PaceLineKey,
  stroke: string,
  radius = 5,
): (props: WorkPaceArtifactDotProps) => ReactElement {
  return (props: WorkPaceArtifactDotProps) => {
    const { cx, cy, payload, index } = props;
    const point = payload as WorkPaceArtifactChartDataPoint | undefined;
    const value = point?.[dataKey];

    if (value == null) return <g key={`empty-${point?.date || index}`} />;

    return (
      <circle
        key={`dot-${point?.date || index}`}
        cx={cx}
        cy={cy}
        r={radius}
        fill={point?.statusColor ?? '#4C6EF5'}
        stroke={stroke}
        strokeWidth={2}
      />
    );
  };
}

/**
 * Renders the Work Pace chart tooltip while suppressing nullified future rows.
 *
 * @param {WorkPaceArtifactTooltipProps} props - Recharts tooltip props plus active chart metadata.
 * @returns {ReactElement | null} Tooltip JSX or null when no concrete data point should be shown.
 */
function ChartTooltip({
  active,
  payload,
  label,
  chartType,
  metricLabel,
  primaryDataKey,
}: WorkPaceArtifactTooltipProps): ReactElement | null {
  if (!active || !payload?.length) return null;

  const data = payload[0]?.payload as WorkPaceArtifactChartDataPoint | undefined;
  if (!data || data[primaryDataKey] == null) return null;

  return (
    <TooltipContainer>
      <TooltipHeader>{`Date: ${formatDate(String(label), { format: 'MMM D, YYYY' })}`}</TooltipHeader>
      <TooltipRow>
        <TooltipLabel>Total:</TooltipLabel>
        <TooltipValue>{`${data.total ?? 0} ${metricLabel}`}</TooltipValue>
      </TooltipRow>
      <TooltipRow>
        <TooltipLabel>{chartType === WorkPaceChartType.BurnDown ? 'Remaining:' : 'Actual:'}</TooltipLabel>
        <TooltipValue>{`${data[primaryDataKey] ?? 0} ${metricLabel}`}</TooltipValue>
      </TooltipRow>
      <TooltipRow>
        <TooltipLabel>{chartType === WorkPaceChartType.BurnDown ? 'Ideal Remaining:' : 'Ideal:'}</TooltipLabel>
        <TooltipValue>{`${data.ideal ?? 0} ${metricLabel}`}</TooltipValue>
      </TooltipRow>
      <TooltipRow>
        <TooltipLabel>Status:</TooltipLabel>
        <TooltipStatus color={data.statusColor ?? '#FA5252'}>{getStatusText(data.status)}</TooltipStatus>
      </TooltipRow>
    </TooltipContainer>
  );
}

const TooltipContainer = styled.div`
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 6px rgb(0 0 0 / 12%);
  font-size: 12px;
`;

const TooltipHeader = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--sky-light);
  padding-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`;

const TooltipLabel = styled.span`
  font-weight: 600;
`;

const TooltipValue = styled.span`
  font-weight: 400;
`;

const TooltipStatus = styled.span<WorkPaceTooltipStatusProps>`
  font-weight: 700;
  color: ${(props) => props.color};
`;

export {
  ChartTooltip,
  buildScopedWorkPaceQuickLinks,
  buildWorkPaceArtifactChartData,
  getInitialChartType,
  getInitialMetric,
  getInitialWorkPeriodIndex,
  getMetricLabel,
  isWorkPacePayload,
  nullifyFuturePaceChartData,
  openArtifactQuickLink,
  renderStatusDot,
};
